/**
 * ExplodingEnemy - Self-destructing enemy that detonates near player
 * 
 * Patrols normally but detects the player within a proximity radius.
 * When player is near, it starts a timed fuse (flashing red) and then
 * explodes, creating a danger zone. Can still be defeated from above.
 * 
 * @module objects/ExplodingEnemy
 */

import Phaser from 'phaser';

/** Exploding enemy states */
const ExplodeState = {
    PATROL: 'patrol',
    ALERT: 'alert',
    FUSE: 'fuse',
    EXPLODE: 'explode'
};

export default class ExplodingEnemy {
    /**
     * @param {Phaser.Scene} scene - The game scene
     * @param {number} x - Spawn X position
     * @param {number} y - Spawn Y position
     * @param {Object} [options] - Configuration options
     * @param {number} [options.detectionRange=140] - Player detection radius
     * @param {number} [options.fuseDuration=1500] - Fuse time in ms before explosion
     * @param {number} [options.explosionRadius=90] - Explosion danger radius
     * @param {number} [options.speed=1.0] - Patrol speed
     * @param {number} [options.walkDist=80] - Patrol range
     */
    constructor(scene, x, y, options = {}) {
        this.scene = scene;
        this.startPos = { x, y };
        this.isDead = false;
        this.type = 'exploding';

        // Configuration
        this.detectionRange = options.detectionRange ?? 140;
        this.fuseDuration = options.fuseDuration ?? 1500;
        this.explosionRadius = options.explosionRadius ?? 90;
        this.speed = options.speed ?? 1.0;
        this.walkDist = options.walkDist ?? 80;

        // State
        this.state = ExplodeState.PATROL;
        this.moveDir = 1;
        this.fuseStartTime = 0;
        this.bounceTimer = 0;
        this.flashTimer = 0;
        this.isFlashing = false;

        // Create visuals and physics
        this.createVisuals(x, y);
        this.createPhysicsBody(x, y);
    }

    /**
     * Create all visual elements
     */
    createVisuals(x, y) {
        // Shadow
        this.shadow = this.scene.add.ellipse(x, y + 22, 32, 8, 0x000000, 0.3);

        // Main body (sphere-like with "danger" markings)
        this.bodyGraphics = this.scene.add.graphics();
        this.drawBody(x, y, 0xff6600);

        // Physics attachment
        this.sprite = this.scene.add.rectangle(x, y, 36, 36, 0x000000, 0);

        // Fuse on top
        this.fuse = this.scene.add.graphics();
        this.drawFuse(x, y);

        // Warning icon (hidden until alert)
        this.warningIcon = this.scene.add.text(x, y - 30, '⚠', {
            fontSize: '16px',
            color: '#ffaa00'
        }).setOrigin(0.5).setAlpha(0);

        // Eyes (round, anxious)
        this.leftEye = this.scene.add.circle(x - 8, y - 4, 5, 0xffffff);
        this.rightEye = this.scene.add.circle(x + 8, y - 4, 5, 0xffffff);
        this.leftPupil = this.scene.add.circle(x - 7, y - 4, 2.5, 0x000000);
        this.rightPupil = this.scene.add.circle(x + 9, y - 4, 2.5, 0x000000);

        // Detection range indicator (very subtle)
        this.rangeIndicator = this.scene.add.circle(x, y, this.detectionRange, 0xff6600, 0.03);
    }

    /**
     * Draw body shape
     * @param {number} x
     * @param {number} y
     * @param {number} color - Current body color
     */
    drawBody(x, y, color) {
        this.bodyGraphics.clear();

        // Glow (subtle)
        this.bodyGraphics.fillStyle(color, 0.1);
        this.bodyGraphics.fillCircle(x, y, 24);

        // Main body
        this.bodyGraphics.fillStyle(color, 1);
        this.bodyGraphics.fillCircle(x, y, 18);

        // Highlight
        this.bodyGraphics.fillStyle(0xffffff, 0.25);
        this.bodyGraphics.fillCircle(x - 5, y - 5, 8);

        // Danger stripes
        this.bodyGraphics.lineStyle(2, 0x000000, 0.3);
        this.bodyGraphics.lineBetween(x - 12, y + 6, x + 12, y + 6);
        this.bodyGraphics.lineBetween(x - 10, y + 10, x + 10, y + 10);

        // Bottom shadow  
        this.bodyGraphics.fillStyle(0x000000, 0.2);
        this.bodyGraphics.fillCircle(x, y + 4, 14);
    }

    /**
     * Draw fuse
     * @param {number} x
     * @param {number} y
     */
    drawFuse(x, y) {
        this.fuse.clear();

        // Fuse wire
        this.fuse.lineStyle(2, 0x444444);
        this.fuse.beginPath();
        this.fuse.moveTo(x, y - 18);
        this.fuse.lineTo(x + 2, y - 24);
        this.fuse.lineTo(x - 1, y - 28);
        this.fuse.lineTo(x + 1, y - 32);
        this.fuse.stroke();

        // Spark at tip (visible during fuse state)
        if (this.state === ExplodeState.FUSE || this.state === ExplodeState.ALERT) {
            this.fuse.fillStyle(0xffff00, 0.8);
            this.fuse.fillCircle(x + 1, y - 32, 3);
            this.fuse.fillStyle(0xff4400, 0.5);
            this.fuse.fillCircle(x + 1, y - 32, 5);
        }
    }

    /**
     * Create physics body
     */
    createPhysicsBody(x, y) {
        const { Bodies } = Phaser.Physics.Matter.Matter;
        const enemyBody = Bodies.circle(x, y, 18, {
            isStatic: false,
            friction: 0.1,
            frictionAir: 0.02,
            density: 0.003,
            label: 'enemy'
        });

        this.scene.matter.add.gameObject(this.sprite, enemyBody);
        this.sprite.setFixedRotation();
        this.sprite.enemyObject = this;
    }

    /**
     * Update with player proximity detection
     * @param {Object} [player] - Player reference for distance check
     */
    update(player) {
        if (this.isDead || !this.sprite.active) return;

        const now = this.scene.time.now;

        switch (this.state) {
            case ExplodeState.PATROL:
                this.updatePatrol(player);
                break;
            case ExplodeState.ALERT:
                this.updateAlert(now);
                break;
            case ExplodeState.FUSE:
                this.updateFuse(now);
                break;
        }

        this.updateVisuals();
    }

    /**
     * Patrol and check for player proximity
     */
    updatePatrol(player) {
        // Direction bounds
        if (this.sprite.x > this.startPos.x + this.walkDist) {
            this.moveDir = -1;
        } else if (this.sprite.x < this.startPos.x - this.walkDist) {
            this.moveDir = 1;
        }

        this.sprite.setVelocityX(this.moveDir * this.speed);
        this.bounceTimer += 0.12;

        // Check player proximity
        if (player && player.sprite && player.sprite.active) {
            const dist = Phaser.Math.Distance.Between(
                this.sprite.x, this.sprite.y,
                player.sprite.x, player.sprite.y
            );

            if (dist < this.detectionRange) {
                this.state = ExplodeState.ALERT;
                this.fuseStartTime = this.scene.time.now;
                this.sprite.setVelocityX(0);
            }
        }
    }

    /**
     * Alert state - brief warning before fuse
     */
    updateAlert(now) {
        const elapsed = now - this.fuseStartTime;
        this.sprite.setVelocityX(0);

        // Show warning
        this.warningIcon.setAlpha(Math.sin(elapsed * 0.01) * 0.5 + 0.5);

        if (elapsed > 400) {
            this.state = ExplodeState.FUSE;
            this.fuseStartTime = now;
        }
    }

    /**
     * Fuse state - counting down to explosion
     */
    updateFuse(now) {
        const elapsed = now - this.fuseStartTime;
        const progress = elapsed / this.fuseDuration;
        this.sprite.setVelocityX(0);

        // Flashing increases in frequency as fuse progresses
        this.flashTimer += 0.15 + progress * 0.4;
        this.isFlashing = Math.sin(this.flashTimer) > 0;

        // Vibration effect
        if (this.sprite.body) {
            const shake = (1 + progress * 3) * (Math.random() - 0.5);
            this.sprite.x += shake;
        }

        // Warning icon pulses
        this.warningIcon.setAlpha(0.6 + Math.sin(elapsed * 0.02) * 0.4);

        if (elapsed >= this.fuseDuration) {
            this.explode();
            return;
        }
    }

    /**
     * Trigger explosion - creates danger zone and self-destructs
     */
    explode() {
        if (this.isDead) return;
        this.isDead = true;

        const x = this.sprite.x;
        const y = this.sprite.y;

        // Explosion flash
        const flash = this.scene.add.circle(x, y, 20, 0xffff00, 1);
        this.scene.tweens.add({
            targets: flash,
            scale: 6,
            alpha: 0,
            duration: 500,
            ease: 'Power2',
            onComplete: () => flash.destroy()
        });

        // Explosion ring
        const ring = this.scene.add.circle(x, y, 10, 0xff4400, 0.5);
        ring.setStrokeStyle(3, 0xff4400);
        this.scene.tweens.add({
            targets: ring,
            scale: this.explosionRadius / 10,
            alpha: 0,
            duration: 600,
            ease: 'Power3',
            onComplete: () => ring.destroy()
        });

        // Debris particles  
        for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2;
            const speed = Phaser.Math.Between(40, 100);
            const colors = [0xff6600, 0xff4400, 0xffaa00, 0xff0000];
            const particle = this.scene.add.rectangle(
                x, y,
                Phaser.Math.Between(4, 12),
                Phaser.Math.Between(4, 12),
                Phaser.Math.RND.pick(colors)
            );

            this.scene.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * speed,
                y: y + Math.sin(angle) * speed + 40,
                alpha: 0,
                rotation: Phaser.Math.Between(-6, 6),
                duration: 800,
                ease: 'Power2',
                onComplete: () => particle.destroy()
            });
        }

        // Smoke puffs
        for (let i = 0; i < 6; i++) {
            const angle = Math.random() * Math.PI * 2;
            const dist = Phaser.Math.Between(10, 40);
            const smoke = this.scene.add.circle(
                x + Math.cos(angle) * dist,
                y + Math.sin(angle) * dist,
                Phaser.Math.Between(8, 16),
                0x555555, 0.4
            );

            this.scene.tweens.add({
                targets: smoke,
                y: smoke.y - 40,
                scale: 2,
                alpha: 0,
                duration: 1000,
                delay: i * 50,
                ease: 'Power1',
                onComplete: () => smoke.destroy()
            });
        }

        // Camera shake
        this.scene.cameras.main.shake(200, 0.008);
        this.scene.cameras.main.flash(100, 255, 100, 0, false);

        // Cleanup
        this.destroyVisuals();
    }

    /**
     * Sync visuals with physics position
     */
    updateVisuals() {
        const x = this.sprite.x;
        const y = this.sprite.y;
        const bounce = Math.sin(this.bounceTimer) * 1.5;

        // Body color changes based on state
        let color = 0xff6600;
        if (this.state === ExplodeState.FUSE) {
            color = this.isFlashing ? 0xff0000 : 0xff6600;
        } else if (this.state === ExplodeState.ALERT) {
            color = 0xff8800;
        }

        this.drawBody(x, y + bounce, color);
        this.drawFuse(x, y + bounce);

        // Eyes
        const pupilOffset = this.moveDir * 2;
        this.leftEye.setPosition(x - 8, y - 4 + bounce);
        this.rightEye.setPosition(x + 8, y - 4 + bounce);
        this.leftPupil.setPosition(x - 8 + pupilOffset, y - 4 + bounce);
        this.rightPupil.setPosition(x + 8 + pupilOffset, y - 4 + bounce);

        // Warning icon
        this.warningIcon.setPosition(x, y - 36 + bounce);

        // Range indicator
        this.rangeIndicator.setPosition(x, y);

        // Shadow
        this.shadow.setPosition(x, this.startPos.y + 22);
    }

    /**
     * Kill (stomped from above) without explosion  
     */
    kill() {
        if (this.isDead) return;
        this.isDead = true;

        const x = this.sprite.x;
        const y = this.sprite.y;

        // Squish effect (defeated, not exploded)
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            const particle = this.scene.add.circle(x, y, 4, 0xff6600, 0.7);
            this.scene.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * 30,
                y: y + Math.sin(angle) * 30,
                alpha: 0,
                duration: 500,
                ease: 'Power2',
                onComplete: () => particle.destroy()
            });
        }

        this.destroyVisuals();
    }

    /**
     * Safely destroy all visuals
     */
    destroyVisuals() {
        const visuals = [
            this.sprite, this.bodyGraphics, this.fuse,
            this.leftEye, this.rightEye,
            this.leftPupil, this.rightPupil,
            this.warningIcon, this.rangeIndicator,
            this.shadow
        ];

        visuals.forEach(v => {
            if (v && v.destroy && !v.destroyed) {
                v.destroy();
            }
        });

        this.sprite = null;
        this.bodyGraphics = null;
    }

    /**
     * Full cleanup
     */
    destroy() {
        if (!this.isDead) {
            this.isDead = true;
        }
        this.destroyVisuals();
    }
}
